/**
 * Product-line audit: which machines the pricing engine can still price from the catalogue, and the
 * control that moves one onto a product line still on sale.
 *
 * Why the screen exists: `PricingCalculationService.ResolveProductLineAsync` only resolves product
 * lines that are ACTIVE, and defaults to `Standard` for a machine whose specifications name none.
 * Retiring a tier therefore takes every machine sold on it out of the catalogue and drops it onto
 * the legacy `component_prices` table, at two to four times the rate — silently, on renewal.
 *
 * The list says WHICH machines cannot be priced and which line of each stack is blocking — cheap, so
 * a page of fifty costs three queries. It prices nothing. The correction itself refuses a product
 * line that cannot price the machine, so no figure is needed on screen to keep that choice safe.
 *
 * Applying writes ONE key — `productline` — onto the VM and each of its live disks and networks, and
 * brings the VM's own record in line with the chosen line: the product line and the pool the admin
 * screens show for it. Nothing about the machine at the provider, its recorded price or its billing
 * history moves; the recorded pool is what the next reinstall or restore sends.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CircleHelp,
  Info,
  CheckCircle2,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  ServerCog,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
import { PermissionGate } from "@/components/common/PermissionGate";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ResourceStatus } from "@/types/admin";
import { adminResourceService } from "@/services/admin-resource.service";
import { adminProductLineService } from "@/services/admin-product-line.service";
import { resourceKeys } from "@/hooks/useResourceQueries";
import type {
  ResourceProductLineAuditRow,
  ResourceProductLineLine,
} from "@/types/admin";
import { formatCurrency } from "@/lib/currency";
import { formatDateRangeForAPI, lastMonthsRange } from "@/lib/date-utils";
import { RESOURCE_STATUS_OPTIONS } from "@/stores/resourceFiltersStore";
import { formatDate } from "@/lib/utils";
import { getPaginationItems } from "@/lib/pagination";
import { toast } from "sonner";

type ColumnId =
  | "machine"
  | "customer"
  | "spec"
  | "productLine"
  | "status"
  | "recorded"
  | "apply";

/**
 * Header and <colgroup> are both generated from this list, so a column can never have a header
 * without a width or shift every width after it by one.
 */
const COLUMNS: Array<{
  id: ColumnId;
  label: string;
  width: string;
  className?: string;
  title?: string;
}> = [
  { id: "machine", label: "Machine", width: "w-[230px]" },
  { id: "customer", label: "Customer", width: "w-[230px]" },
  { id: "spec", label: "Spec", width: "w-[120px]" },
  {
    id: "productLine",
    label: "Product line",
    width: "w-[170px]",
    title:
      "The product line the machine is priced on. Pool is the WiService pool recorded for it, set to the chosen line's pool on a move.",
  },
  {
    id: "status",
    label: "Status",
    width: "w-[200px]",
    title:
      "Whether that product line is still on sale. Red lines fall back to the legacy component prices, which blocks renewal and upgrade.",
  },
  {
    id: "recorded",
    label: "Recorded",
    width: "w-[130px]",
    className: "text-right",
    title: "The monthly rate stored on the VM row. Not changed here.",
  },
  {
    id: "apply",
    label: "Move to",
    width: "w-[200px]",
    className:
      "sticky right-0 z-20 bg-panel shadow-[inset_1px_0_0_0_var(--border)]",
  },
];

/** Short names for the billed lines of a machine. */
const LINE_TYPE_LABELS: Record<string, string> = {
  VirtualMachine: "VM",
  Storage: "Disk",
  Network: "Net",
};

/**
 * The search box commits after this long. Same 750ms every other admin list uses, so the spinner
 * inside the box means the same thing here as it does there.
 */
const SEARCH_DEBOUNCE_MS = 750;

export function ProductLineAuditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Local state rather than a persisted filters store: this is a repair tool an operator opens for
  // a task and closes, not one of the list screens they live in. A filter kept in sessionStorage
  // would quietly hide machines from the next person who opens it to answer "are we clean?".
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [status, setStatus] = useState<ResourceStatus | "All">("All");
  // Opens on the last six months, like the Resources list; clearing the chip searches all time.
  const [dateRange, setDateRange] = useState<DateRangeValue>(() =>
    lastMonthsRange(),
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [chosen, setChosen] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // The lines still on sale, fetched once for the page: the Apply picker has to be usable before
  // any row has been priced, and every row offers the same choices.
  const productLinesQuery = useQuery({
    queryKey: ["admin", "product-lines", "active"],
    queryFn: () =>
      adminProductLineService.getProductLines({
        isActive: true,
        page: 1,
        limit: 50,
      }),
    staleTime: 5 * 60 * 1000,
  });
  const productLines = productLinesQuery.data?.data ?? [];

  // Both ends or neither, as on the Resources list: the backend ignores half a range.
  const { fromDate: createdFrom, toDate: createdTo } =
    dateRange.from && dateRange.to
      ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [
      "admin",
      "product-line-audit",
      { page, pageSize, search, onlyIssues, status, createdFrom, createdTo },
    ],
    queryFn: () =>
      adminResourceService.getProductLineAudit({
        page,
        pageSize,
        search: search || undefined,
        onlyIssues,
        status: status === "All" ? undefined : status,
        createdFrom,
        createdTo,
      }),
    placeholderData: (previous) => previous,
  });

  const apply = useMutation({
    mutationFn: ({
      resourceId,
      productLineId,
    }: {
      resourceId: string;
      productLineId: string;
    }) => adminResourceService.updateProductLine(resourceId, productLineId),
    onSuccess: (result) => {
      // Naming the lines matters: the disk and the network are separately priced, and the whole
      // reason this screen exists is that correcting the VM alone left them behind.
      const written = result.updatedLines.length;
      toast.success(`${result.name} is now priced on ${result.tier}`, {
        description:
          written === 0
            ? // Not "nothing changed": a machine moved before its own record followed the move
              // gets that record fixed by this call, with no billed line left to write.
              "Every billed line was already on that product line. The machine's own record matches it."
            : `Updated ${written} billed line${written === 1 ? "" : "s"}: ${result.updatedLines
                .map((line) => line.name)
                .join(", ")}. Nothing already ordered or billed was changed.`,
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "product-line-audit"],
      });
      // The move also sets the line the Resources list filters on and the product line and pool
      // the list and the detail page show, so their cached copies are stale.
      void queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: resourceKeys.detail(result.resourceId),
      });
    },
    // The service throws the API's own message, which names the refusal (a product line
    // that is no longer on sale, a concurrent write) — worth showing verbatim.
    onError: (err: Error) =>
      toast.error(err.message || "The product line could not be changed."),
  });

  const rows = data?.data ?? [];
  const totalItems = data?.pagination.totalItems ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const currentPage = data?.pagination.currentPage ?? page;

  // Nothing to show yet is a skeleton; a refetch over rows already on screen is the pill below.
  const isReloading = isLoading && rows.length === 0;
  const isSearchPending = searchInput.trim() !== search;
  // The footer's reload is the screen's "start over", as on the other admin lists: every filter back
  // to its opening value, then a fresh fetch. Invalidating (not refetch) also covers the case where
  // nothing changed, so the key is the same and no new query would otherwise run.
  const resetFiltersAndReload = () => {
    setSearchInput("");
    setSearch("");
    setOnlyIssues(true);
    setStatus("All");
    setDateRange(lastMonthsRange());
    setPage(1);
    void queryClient.invalidateQueries({
      queryKey: ["admin", "product-line-audit"],
    });
  };

  const hasNarrowingFilters =
    search !== "" ||
    status !== "All" ||
    Boolean(dateRange.from && dateRange.to);

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">Product Line Audit</h1>
          <p className="text-sm text-muted-foreground">
            Machines on a product line that is no longer on sale cannot be
            renewed or upgraded until they are moved to another line.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <HowThisWorks />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/tenants")}
            className="h-8 min-h-8 gap-2 px-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tenants
          </Button>
        </div>
      </div>

      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center pt-4">
            <span className="rounded-full border bg-panel px-3 py-1 text-xs text-muted-foreground shadow-sm">
              Updating…
            </span>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="relative w-full sm:w-[360px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.currentTarget.value)}
                placeholder="VM name, VM uuid, tenant id, email, phone"
                className="h-9 pl-10 pr-9"
              />
              {isSearchPending && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>

            <div className="flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-md border px-2">
              <Switch
                id="onlyIssues"
                checked={onlyIssues}
                onCheckedChange={(checked) => {
                  setOnlyIssues(checked);
                  setPage(1);
                }}
              />
              <Label htmlFor="onlyIssues" className="cursor-pointer text-sm">
                Only broken
              </Label>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What does Only broken mean?"
                      className="cursor-help text-muted-foreground hover:text-foreground"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Show only machines where the VM, disk or network is on a
                    product line that is no longer on sale. Their renewal and
                    upgrade are blocked until they are moved to another line.
                    Turn it off to see every machine.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="order-3 flex w-full min-w-0 items-center gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:justify-end sm:self-center">
              {search && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => setSearchInput("")}
                    aria-label="Clear search filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {status !== "All" && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status:{" "}
                  {
                    RESOURCE_STATUS_OPTIONS.find(
                      (option) => option.value === status,
                    )?.label
                  }
                  <button
                    type="button"
                    onClick={() => {
                      setStatus("All");
                      setPage(1);
                    }}
                    aria-label="Clear status filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {dateRange.from && dateRange.to && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Created: {formatDate(dateRange.from)} –{" "}
                  {formatDate(dateRange.to)}
                  <button
                    type="button"
                    onClick={() => {
                      setDateRange({});
                      setPage(1);
                    }}
                    aria-label="Clear created date filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover>
              {/* Icon only: title gives the hover tooltip, aria-label the screen-reader name. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 shrink-0 p-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="productLineAuditStatusFilter">
                    VM Status
                  </Label>
                  <Select
                    value={status}
                    onValueChange={(value) => {
                      setStatus(value as ResourceStatus | "All");
                      setPage(1);
                    }}
                  >
                    <SelectTrigger id="productLineAuditStatusFilter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="productLineAuditCreatedFilter">
                    Created Date
                  </Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => {
                      setDateRange(range);
                      setPage(1);
                    }}
                    placeholder="Select date range"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => void refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {isReloading ? (
            <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              {onlyIssues ? (
                <>
                  <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-green-600 dark:text-green-400" />
                  <h3 className="mb-2 text-lg font-semibold">
                    {hasNarrowingFilters
                      ? "No broken machines match these filters"
                      : "Every machine resolves a product line"}
                  </h3>
                  <p className="text-muted-foreground">
                    {hasNarrowingFilters
                      ? "Clear the filters to check the whole fleet."
                      : "Nothing here is falling back to the legacy component prices."}
                  </p>
                </>
              ) : (
                <>
                  <ServerCog className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No machines found
                  </h3>
                  <p className="text-muted-foreground">
                    Try a different search term or clear the filters.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
              <Table className="table-fixed border-collapse [&_td]:border-x [&_th]:border-x [&_td:first-child]:border-l-0 [&_th:first-child]:border-l-0 [&_td:last-child]:border-r-0 [&_th:last-child]:border-r-0">
                <colgroup>
                  {COLUMNS.map((column) => (
                    <col key={column.id} className={column.width} />
                  ))}
                </colgroup>
                <TableHeader className="sticky top-0 z-30 bg-panel">
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <TableHead
                        key={column.id}
                        className={column.className}
                        title={column.title}
                      >
                        {column.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <AuditRow
                      key={row.resourceId}
                      row={row}
                      productLines={productLines}
                      chosen={chosen[row.resourceId] ?? ""}
                      onChoose={(slug) =>
                        setChosen((current) => ({
                          ...current,
                          [row.resourceId]: slug,
                        }))
                      }
                      onApply={(slug) => {
                        // The picker works in slugs, which is what a row reports; the API takes
                        // the line's id and reads everything else from the catalogue itself.
                        const line = productLines.find(
                          (candidate) => candidate.slug === slug,
                        );
                        if (line) {
                          apply.mutate({
                            resourceId: row.resourceId,
                            productLineId: line.id,
                          });
                        }
                      }}
                      busy={
                        apply.isPending &&
                        apply.variables?.resourceId === row.resourceId
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value, 10));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
                <Button
                  onClick={resetFiltersAndReload}
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                >
                  <RefreshCw
                    className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
                  />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** One machine, with the three prices that make the decision and the control that acts on it. */
function AuditRow({
  row,
  productLines,
  chosen,
  onChoose,
  onApply,
  busy,
}: {
  row: ResourceProductLineAuditRow;
  productLines: Array<{ slug: string; name: string }>;
  chosen: string;
  onChoose: (slug: string) => void;
  onApply: (slug: string) => void;
  busy: boolean;
}) {
  // Apply is never disabled for "already on this line". The pool a line is configured with can
  // change after a machine was put on it, and this screen cannot know that pool: applying the line
  // a healthy machine is already on is how its recorded pool is brought back to the configured one.
  // The backend writes nothing when every line and the record already match.
  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="truncate font-medium" title={row.name}>
          {row.name}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <MachineStatus status={row.status} />
          <span className="text-xs text-muted-foreground">
            {row.expiresAt
              ? `Expires ${formatDate(row.expiresAt)}`
              : "No expiry"}
          </span>
        </div>
        <div
          className="mt-1 truncate font-mono text-[11px] text-muted-foreground"
          title={row.vmUuid ?? undefined}
        >
          {row.vmUuid ?? "No provider UUID"}
        </div>
      </TableCell>

      <TableCell className="align-top">
        <div
          className="truncate font-medium"
          title={`Tenant ID: ${row.tenantId}`}
        >
          {row.tenantName ?? "—"}
        </div>
        <div
          className="mt-1 truncate text-xs text-muted-foreground"
          title={row.ownerEmail ?? undefined}
        >
          {row.ownerEmail ?? "—"}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {row.ownerPhoneNumber ?? "—"}
        </div>
      </TableCell>

      <TableCell className="align-top text-sm">
        <div>
          {row.cores ?? "—"} vCPU · {row.memoryGb ?? "—"} GB
        </div>
        <div className="text-xs text-muted-foreground">
          {row.diskGb ?? "—"} GB disk
        </div>
      </TableCell>

      <TableCell className="align-top">
        <ProductLineName row={row} />
        {/* The pool recorded for the machine and, in the tooltip, the line its own record names.
            A move sets both to the chosen line; the machine itself is not migrated. On a row with
            no pricing product line the recorded line is usually the only record of what was sold. */}
        <div
          className="mt-1 truncate text-xs text-muted-foreground"
          title={
            row.provisioningProductLine
              ? `Recorded on ${row.provisioningProductLine}`
              : undefined
          }
        >
          Pool: <span className="font-mono">{row.poolName ?? "—"}</span>
        </div>
      </TableCell>

      <TableCell className="align-top">
        <ProductLineStatus row={row} />
      </TableCell>

      <TableCell className="align-top text-right">
        <div className="text-sm tabular-nums">
          {formatCurrency(row.recordedMonthlyPrice, row.currency)}
        </div>
        <div className="text-xs text-muted-foreground">VM, per month</div>
      </TableCell>

      <TableCell className="sticky right-0 z-10 bg-card align-top shadow-[inset_1px_0_0_0_var(--border)]">
        <PermissionGate roles="SuperAdmin">
          <div className="flex items-center gap-1.5">
            <Select value={chosen} onValueChange={onChoose} disabled={busy}>
              <SelectTrigger className="h-8 min-w-0 flex-1 text-sm data-[placeholder]:text-placeholder">
                <SelectValue placeholder="Choose line" />
              </SelectTrigger>
              <SelectContent>
                {productLines.map((line) => (
                  <SelectItem key={line.slug} value={line.slug}>
                    {line.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8 shrink-0 px-3 text-sm"
              disabled={!chosen || busy}
              onClick={() => onApply(chosen)}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
            </Button>
          </div>
        </PermissionGate>
      </TableCell>
    </TableRow>
  );
}

/**
 * Which product line the machine is on. For a broken machine that is the retired tier the blocking
 * lines ask for — the one an operator has to move it off — not the line it would move to.
 */
function ProductLineName({ row }: { row: ResourceProductLineAuditRow }) {
  if (row.issue === "None") {
    return (
      <div className="font-medium capitalize">{row.resolvedProductLine}</div>
    );
  }

  const blockingTiers: string[] = [];
  for (const line of row.lines) {
    const blocking = line.resolvedProductLine === null;
    if (blocking && !blockingTiers.includes(line.requestedTier)) {
      blockingTiers.push(line.requestedTier);
    }
  }

  return (
    <div>
      <div className="font-medium">{blockingTiers.join(", ")}</div>
      {row.issue === "NotStated" && (
        <div className="text-xs text-muted-foreground">
          Not recorded (default)
        </div>
      )}
    </div>
  );
}

/**
 * Whether the machine's product line is still on sale, and which of its billed lines block it. A
 * machine whose VM reads clean can still be refused by its own disk, so every line is shown.
 */
function ProductLineStatus({ row }: { row: ResourceProductLineAuditRow }) {
  let badge = (
    <Badge variant="outline" className="gap-1 border-ok/30 text-ok-ink">
      <CheckCircle2 className="h-3 w-3" />
      On sale
    </Badge>
  );

  if (row.issue !== "None") {
    badge = (
      <Badge
        variant="outline"
        className="gap-1 border-destructive/30 text-destructive-ink"
      >
        <AlertTriangle className="h-3 w-3" />
        Retired
      </Badge>
    );
  }

  return (
    <div className="space-y-1.5">
      {badge}
      <div className="flex flex-wrap gap-1">
        {row.lines.map((line) => (
          <LineChip key={line.resourceId} line={line} />
        ))}
      </div>
    </div>
  );
}

/** One billed line — red when it falls back to the legacy component prices. */
function LineChip({ line }: { line: ResourceProductLineLine }) {
  const typeLabel = LINE_TYPE_LABELS[line.type] ?? line.type;
  const blocking = line.resolvedProductLine === null;

  let className =
    "inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground";
  let title = `${line.name}: priced on ${line.resolvedProductLine}`;
  if (blocking) {
    className =
      "inline-flex items-center rounded border border-destructive/30 bg-destructive-soft px-1.5 py-0.5 text-[11px] text-destructive-ink";
    title = `${line.name}: stored "${line.storedProductLine ?? "none"}", falls back to the legacy component prices`;
  }

  return (
    <span className={className} title={title}>
      {typeLabel}
    </span>
  );
}

/** Text colour and dot per machine status: a quiet label rather than a filled badge. */
const MACHINE_STATUS_STYLES: Record<
  ResourceStatus,
  { dot: string; text: string }
> = {
  Running: { dot: "bg-ok", text: "text-ok-ink" },
  Stopped: { dot: "bg-muted-foreground", text: "text-muted-foreground" },
  Suspended: { dot: "bg-warn", text: "text-warn-ink" },
  Error: { dot: "bg-destructive", text: "text-destructive-ink" },
  Provisioning: { dot: "bg-info", text: "text-info-ink" },
  Pending: { dot: "bg-info", text: "text-info-ink" },
  Terminating: { dot: "bg-warn", text: "text-warn-ink" },
  Terminated: { dot: "bg-muted-foreground", text: "text-muted-foreground" },
  Decommissioning: { dot: "bg-warn", text: "text-warn-ink" },
};

function MachineStatus({ status }: { status: ResourceStatus }) {
  const style = MACHINE_STATUS_STYLES[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${style.text}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${style.dot}`}
        aria-hidden="true"
      />
      {status}
    </span>
  );
}

/** The screen's own instructions, one click away rather than a paragraph on every visit. */
function HowThisWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 min-h-8 gap-2 px-2 text-sm"
        >
          <CircleHelp className="h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] text-sm">
        <h3 className="mb-2 font-semibold">How this works</h3>
        <ol className="list-decimal space-y-2 pl-4 text-muted-foreground">
          <li>
            A product line that is{" "}
            <span className="text-foreground">no longer on sale</span> cannot
            price a machine, so the engine falls back to the legacy component
            prices (2-4x higher). Renewal and upgrade are refused for those
            machines.
          </li>
          <li>
            <span className="text-foreground">Product line</span> shows the
            retired line the machine is on.{" "}
            <span className="text-foreground">Status</span> shows which billed
            lines (VM, Disk, Net) are blocking it.
          </li>
          <li>
            Pick a line under <span className="text-foreground">Move to</span>{" "}
            and press Apply. It is written to the VM, its disk and its network
            together.
          </li>
          <li>
            The machine&apos;s product line and pool follow the chosen line on
            every admin screen. The VM itself is not migrated: the new pool is
            what its next reinstall or restore uses.
          </li>
          <li>
            Applying the line a machine is already on is allowed: it sets the
            recorded pool to the one that line is configured with now. Turn off
            Only broken to reach those machines.
          </li>
          <li>
            Nothing else changes: not the recorded price, orders or billing
            cycles. The next renewal is quoted at the new line&apos;s current
            rates.
          </li>
          <li>
            Apply is refused when the chosen line cannot price the
            machine&apos;s configuration (for example backup slots it does not
            allow). Change the configuration first.
          </li>
        </ol>
      </PopoverContent>
    </Popover>
  );
}

export default ProductLineAuditPage;
